import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { LoadFailed } from '../components/QueryState';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  canViewFinanceDashboard,
  canViewTrustPosition,
  comparisonRange,
  formatDateRange,
  matchingRangeKey,
} from '@promanage/core';
import { todayIso } from '../features/property/today';
import { TopBar } from '../components/TopBar';
import { DashboardControls } from '../features/dashboard/DashboardControls';
import { DashboardLayoutMenu } from '../features/dashboard/DashboardLayoutMenu';
import { dashboardRedirect } from '../features/dashboard/layout';
import { dashboardSearch, resolveRange, type DashboardSearch } from '../features/dashboard/period';
import { TrustPosition } from '../features/dashboard/TrustPosition';
import { UpcomingPayments } from '../features/dashboard/UpcomingPayments';
import { UnmatchedQueue } from '../features/dashboard/UnmatchedQueue';
import { ArrearsList } from '../features/dashboard/ArrearsList';
import {
  CollectionRateCard,
  PaymentsCard,
  TransactionsCard,
} from '../features/dashboard/ActivityCards';
import { ShortcutsLink } from '../features/shortcuts/ShortcutKeys';
import { MonthClosePanel } from '../features/dashboard/MonthClosePanel';
import { ActivityFeed } from '../features/dashboard/ActivityFeed';
import { CardSkeleton } from '../features/dashboard/CardSkeleton';
import {
  BillsTile,
  BondsTile,
  RunsTile,
  SuspenseTile,
  ThreeWayWidget,
} from '../features/dashboard/FinanceTiles';
import { trpc } from '../lib/trpc';
import { resolveSession, useSession } from '../features/auth/session';

export const Route = createFileRoute('/')({
  /*
   * THE PERIOD IS IN THE URL, not in component state, for the reasons every other filter in this
   * product is: it is a thing somebody reloads, bookmarks and sends to a colleague — "look at
   * August, our transactions doubled" is a link or it is a phone call.
   *
   * A PRESET IS STORED AS ITS NAME. `?range=quarter_to_date` is still this quarter tomorrow
   * morning; the dates it resolves to are not, and a bookmark would quietly go on reporting a
   * window that ended weeks ago. Typed dates are stored as dates, because that is what they are.
   *
   * The parsing itself is `dashboardSearch` in ./features/dashboard/period, beside the resolver
   * that turns it into dates — out here so both are unit-tested, since a route file is not
   * importable without registering a route.
   */
  validateSearch: (search: Record<string, unknown>): DashboardSearch => dashboardSearch(search),
  /*
   * THE DASHBOARD OPENS ON THE LAYOUT THIS PERSON CHOSE — `users.dashboard_layout`, on the session
   * the shell has already loaded, so this costs no request. `dashboardRedirect` says which
   * visits count; a link carrying a period is honoured as it stands.
   */
  beforeLoad: async ({ context, location, search }) => {
    const session = await resolveSession(context.queryClient);
    // No session is the root guard's business — it sends them to sign in.
    if (session === null) return;
    const visit = {
      searchStr: location.searchStr,
      layout: session.dashboardLayout,
      mayOpenFinance: canViewFinanceDashboard(session.roles),
    };
    const elsewhere = dashboardRedirect(visit);
    if (elsewhere !== null) {
      // The period comes along: the other overviews read the same `?range=`.
      throw redirect({ to: elsewhere, search, replace: true });
    }
  },
  loaderDeps: ({ search }) => dashboardSearch(search),
  // One request now. The side column used to be the portfolio tree, a second and much larger read;
  // the month-close panel that replaced it rides on the summary.
  loader: async ({ context, deps }) => {
    await context.queryClient.prefetchQuery(
      trpc.dashboard.summary.queryOptions(deps, { staleTime: 30_000 }),
    );
  },
  component: Dashboard,
});

function Dashboard() {
  const session = useSession();
  const roles = session?.roles ?? [];
  const mayViewPosition = canViewTrustPosition(roles);
  const navigate = useNavigate({ from: Route.fullPath });

  /*
   * PARSED AGAIN HERE, and that is not belt-and-braces — it is the only parse that binds.
   *
   * `validateSearch` above types the search for `navigate` and drops what it does not recognise,
   * but the router does not act on the dropping: it returns `{}` for `?range=lastmonth` and
   * `Route.useSearch()` still hands back `{ range: 'lastmonth' }`. So the typed object below is a
   * compile-time promise the runtime does not keep, and this page sends it straight to an API
   * whose schema would refuse the value. Parse once, use the result for both.
   */
  const params = dashboardSearch(Route.useSearch());

  const today = todayIso();

  const summary = useQuery(
    // Half a minute: the overview is left open all day, and every figure on it moves when
    // somebody in the next room posts a receipt. Short enough to be current on a glance back,
    // long enough that switching between tabs does not re-run the arrears pass.
    //
    // `search` IS THE QUERY KEY, so changing the period fetches rather than reusing the cached
    // answer for a different window — and stepping back through a few periods is instant, because
    // each one is cached under its own key.
    trpc.dashboard.summary.queryOptions(params, {
      meta: { errorTitle: 'Could not load the overview' },
      staleTime: 30_000,
      /*
       * THE PAGE MUST NOT BLANK WHEN THE PERIOD CHANGES. Most of the cards here do not depend
       * on the period at all — the trust position, the three-way, the arrears and the queue — and
       * throwing them into skeletons to answer a question about August is both slower to read and
       * a lie about what was in flight. The previous answer stays on screen, headings and figures
       * together, until the new one lands.
       */
      placeholderData: keepPreviousData,
    }),
  );

  const data = summary.data;

  /*
   * THE SERVER'S ANSWER WINS. The figures were computed from the range it echoed back — already
   * clamped to today — so every heading on this page is written from that rather than from what
   * the URL asked for, and while the previous answer is still on screen the headings describe
   * THAT period rather than the one being fetched.
   */
  const asked = resolveRange(params, today);
  const range = data?.range ?? asked;
  const comparison = data?.comparison ?? comparisonRange(range);
  const rangeLabel = formatDateRange(range);

  /*
   * A new period is on its way and what is on screen answers the old one.
   *
   * Derived from the DATES rather than from `isFetching`, which is also true of the background
   * refetch every thirty seconds — dimming the page four times a minute on a dashboard somebody
   * leaves open all day would be its own defect.
   */
  const awaitingPeriod = data !== undefined && (range.from !== asked.from || range.to !== asked.to);

  /*
   * `replace`, so a morning spent stepping through months does not bury the page somebody
   * arrived from under a dozen history entries. Back still leaves the dashboard, which is what
   * the button is for.
   */
  const chooseRange = (next: DashboardSearch) => {
    void navigate({ search: next, replace: true });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Overview"
        /*
         * NO SUBTITLE. It used to say which figures the period moves; the trend row's own heading —
         * "Over 1 – 30 Sep, compared with …" — now says it where the figures are, and the period
         * label sits on every card that obeys it. Saying it a third time up here was a sentence on
         * every visit for a fact learnt once.
         */
        actions={
          <>
            <DashboardLayoutMenu current="finance" search={params} />
            <DashboardControls
              range={range}
              comparison={comparison}
              activeKey={matchingRangeKey(range, today)}
              onChange={chooseRange}
            />
          </>
        }
      />

      {/*
       * THE SIDE COLUMN IS THE MONTH CLOSE, and it used to be the portfolio tree. The tree is
       * navigation for somebody whose job is houses — it stays on the management overview, which
       * is that person's page. This page opens for the accounts and trust roles, whose job runs on
       * a monthly cycle no card shows: reconcile through the last day, pay the owners, produce the
       * statements, sign. A sticky column suits it because it is the one thing worth having in
       * view whatever row of cards is being read. Under it, filling the rest of the column, is the
       * activity feed: what changed, and who changed it.
       *
       * Below `lg` there is no column: both follow the cards, in the page's own flow.
       */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[var(--panel-width)_minmax(0,1fr)]">
        <SideColumn>
          <div className="shrink-0">
            {data ? (
              <MonthClosePanel
                close={data.monthClose}
                today={today}
                mayLock={session?.mayClosePeriods ?? false}
              />
            ) : (
              <CardSkeleton className="h-96" />
            )}
          </div>
          {/* A fixed height below `lg`, where there is no column to fill and the list would
              otherwise run the length of forty rows. */}
          <div className="flex h-[28rem] flex-col lg:h-auto lg:min-h-0 lg:flex-1">
            {data ? (
              <ActivityFeed rows={data.recentActivity} today={today} />
            ) : (
              <CardSkeleton className="flex-1" />
            )}
          </div>
        </SideColumn>

        <div className="flex min-w-0 flex-col gap-4">
          {summary.error ? (
            <LoadFailed inline retry={() => void summary.refetch()} retrying={summary.isFetching} />
          ) : null}

          {/* 1. Do the books balance? */}
          {data ? <ThreeWayWidget data={data} /> : <CardSkeleton className="h-[6.5rem]" />}

          {/*
           * EVERY TILE SAYS SOMETHING NO CARD BELOW IT DOES. The row used to repeat three of them —
           * the trust balance the strip shows, the unmatched count the queue's header shows, the
           * arrears total the arrears card shows — so a quarter of the figures on the page were the
           * same numbers twice. What is here now is suspense, which has no card, and the three
           * obligations the upcoming-payments card only previews: each tile is the WHOLE of its
           * kind, where the card lists the soonest four of all of them together.
           */}
          {/* Two across even on a phone: one tile per row spent the first screen on four numbers. */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
            {data ? (
              <>
                <SuspenseTile data={data} />
                <BondsTile data={data} />
                <BillsTile data={data} />
                <RunsTile data={data} />
              </>
            ) : (
              [0, 1, 2, 3].map((tile) => <CardSkeleton key={tile} className="h-[6.75rem]" />)
            )}
          </div>

          {/*
           * 2. What needs me today? — SECOND, straight under whether the books balance. These are
           * the queues an operator actually works, and principle 3's 10% is this row; it used to sit
           * at the foot of the page, under the charts, which is the order for somebody who has come
           * to look rather than somebody who has come to do.
           */}
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            {data ? (
              <UnmatchedQueue
                rows={data.unmatched.rows}
                total={data.unmatched.count}
                valueCents={data.unmatched.valueCents}
              />
            ) : (
              <CardSkeleton className="h-64" />
            )}
            {data ? (
              <ArrearsList
                rows={data.arrears.rows}
                totalCents={data.arrears.totalCents}
                tenancyCount={data.arrears.tenancyCount}
              />
            ) : (
              <CardSkeleton className="h-64" />
            )}
          </div>

          {/* 3. What is the position? */}
          {/*
           * The position card is `trust_admin`'s. A property manager's job is a property, and a
           * donut summing every owner's money is neither theirs to act on nor useful to them —
           * see `canViewTrustPosition`, which is a presentation rule and says so. The strip above
           * stays for everyone: whether the books balance is the agency's business, not one
           * role's.
           *
           * When it is hidden the row collapses to one column rather than leaving a gap where the
           * card was. Upcoming payments is a card, not half a layout.
           */}
          <div
            className={
              mayViewPosition
                ? 'grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]'
                : 'grid grid-cols-1 gap-4'
            }
          >
            {mayViewPosition ? (
              data ? (
                <TrustPosition
                  groups={data.position.groups}
                  periodStartsOn={data.period?.startsOn ?? null}
                  collection={data.collection}
                  feesEarnedCents={data.feesEarnedCents}
                  rangeLabel={rangeLabel}
                />
              ) : (
                <CardSkeleton className="h-64" />
              )
            ) : null}
            {data ? (
              <UpcomingPayments
                rows={data.upcoming.rows}
                count={data.upcoming.count}
                totalCents={data.upcoming.totalCents}
              />
            ) : (
              <CardSkeleton className="h-64" />
            )}
          </div>

          {/* 4. How are we trending? */}
          {/*
           * THE ONLY ROW THE PERIOD MOVES, and it says so on its own heading rather than leaving
           * an operator to work out which cards responded to the bar. Everything above is a
           * balance or a queue as at now. The comparison's dates are spelled out because a
           * "vs previous" whose span the reader cannot see is a number they cannot check.
           */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pt-1">
            <h2 className="text-heading font-bold tracking-[-0.014em]">Over {rangeLabel}</h2>
            <span className="text-label text-ink-muted">
              compared with {formatDateRange(comparison)}
            </span>
          </div>

          {/*
           * `aria-busy` and a dim while a new period is being fetched, on the cards that will
           * change and not on the ones that will not. The figures under it are still true — they
           * are the previous period's, and the heading above says which — so this is "these are
           * about to be replaced", not "these are wrong".
           *
           * TWO COLUMNS, not three. The third was the insight card: sample data under an amber
           * "sample" note — the loudest thing on the page, about nothing real, and amber is this
           * page's word for "act on this". It comes back when the matcher can write one.
           */}
          <div
            aria-busy={awaitingPeriod}
            className={`grid grid-cols-1 gap-4 transition-opacity duration-[var(--duration-fast)] lg:grid-cols-2 ${
              awaitingPeriod ? 'opacity-60' : ''
            }`}
          >
            {data ? (
              <CollectionRateCard months={data.collectionRate} today={today} />
            ) : (
              <CardSkeleton className="h-64" />
            )}
            <div className="flex flex-col gap-4">
              {data ? (
                <>
                  <TransactionsCard activity={data.activity.bankLines} rangeLabel={rangeLabel} />
                  <PaymentsCard activity={data.activity.rentReceipts} rangeLabel={rangeLabel} />
                </>
              ) : (
                <>
                  <CardSkeleton className="h-[7.5rem]" />
                  <CardSkeleton className="h-[7.5rem]" />
                </>
              )}
            </div>
          </div>

          <ShortcutsLink />
        </div>
      </div>
    </div>
  );
}

/**
 * The side column — month close over the activity feed — stuck below the bar from `lg`.
 *
 * `sticky` rather than a shell-level sibling, because the bar above it is sticky inside the same
 * scrollport and there is no CSS for "stick below whatever is already stuck". Hence
 * `--topbar-height`, which TopBar measures and publishes precisely so this does not have to guess:
 * `top` has to land on the column's own resting position to the pixel, or it hops the instant the
 * page starts scrolling.
 *
 * THE HEIGHT IS THE VIEWPORT'S, so the feed's `flex-1` has something to fill and the column ends
 * level with the screen rather than in a strip of empty ground. It is the other half of the same
 * sum as `top`: a sticky box cannot leave its containing block, so one sized any taller is shoved
 * back up as the bottom of the grid arrives. The room is 100dvh less <main>'s 1rem, the bar and its
 * 1rem margin (all already in `top`), this page's `pb-4` and <main>'s bottom padding — which lands
 * the column's bottom edge on the content column's.
 *
 * WITH A FLOOR. On a short window the viewport sum would leave the feed a sliver under the month
 * close, so the column never drops below 30rem; there, the foot of the feed is reached by scrolling
 * the page, which is the lesser evil than a list two rows tall.
 *
 * `dvh` is safe at this breakpoint: there is no mobile browser chrome to collapse at `lg`. Below it
 * the column is `order-last` and in the page's flow, so the reconciliation strip — the first thing
 * this page answers — stays at the top of a phone's screen.
 */
function SideColumn({ children }: { children: React.ReactNode }) {
  return (
    <div className="order-last flex flex-col gap-4 lg:sticky lg:top-[calc(var(--topbar-height)+1rem)] lg:order-none lg:h-[max(30rem,calc(100dvh-var(--topbar-height)-4rem))]">
      {children}
    </div>
  );
}
